import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute, ParamMap, Router } from "@angular/router";
import { TranslocoService } from '@jsverse/transloco';
import { NgxSpinnerService } from 'ngx-spinner';
import { DocumentSigningOrder } from '../../models/e-sign/document-signing-recipient.model';
import { OpenSignerViewRequest } from '../../models/open-signer-view-request.model';
import { BasePortalService } from "../../services/base-portal.service";
import { NavigationService } from "../../services/navigation.service";
import { NotificationService } from '../../services/notification.service';
import { DocumentSigningVendor } from '../../models/e-sign/document-signing-recipient.model';
import { EnvironmentService } from 'projects/shared/services/environment.service';
import { LocalStorageService } from '../../services/local-storage.service';
import { ESignCompletionContext } from '../../models/e-sign/e-sign-completion-context.model';

@Component({
    selector: 'e-sign',
    templateUrl: './e-sign.component.html',
    styleUrls: ['./e-sign.component.scss'],
    standalone: false
})
export class EsignComponent implements OnInit, OnDestroy {

  appId: number;

  eSignOrder: DocumentSigningOrder = new DocumentSigningOrder();

  signerViewUrl: SafeResourceUrl;

  isLoading: boolean = false;
  isAgent: boolean = false;

  private _activeTaskId: number | null = null;
  private _messageHandlerRegistered: boolean = false;

  private readonly _messageHandler = (event: MessageEvent) => {
    if (!event || !event.data) {
      return;
    }

    if (event.data.shouldCloseEsignDocIframe) {
      setTimeout(() => {
        this._router.navigate([`${this.isAgent ? 'agent' : 'borrower'}-portal/loans`, this.appId], { queryParams: { companyGuid: this._navigationService.companyGuid } });
      }, 2000);
      return;
    }

    if (event.data.eSignVendorSigningEnded) {
      // This outcome handoff comes from our own confirmation route rendered inside the signing iframe, so it
      // must be same-origin AND originate from that iframe; the cross-origin vendor signing page - or any
      // other embedding window - must not be able to drive the signing outcome. Binding to the active task
      // (rather than a value closed over per listener) keeps a stale listener from completing a task the
      // signer never signed.
      if (event.origin !== window.location.origin) {
        return;
      }
      const signingIframe = document.getElementById('signing-iframe') as HTMLIFrameElement | null;
      if (!signingIframe || event.source !== signingIframe.contentWindow) {
        return;
      }
      if (this._activeTaskId === null) {
        return;
      }
      this.reportVendorSigningOutcome(this._activeTaskId, event.data.action, event.data.vendorQueryParameters);
      return;
    }

    if (event.data.authTokenRequested) {
      this.answerAuthTokenRequest(event);
      return;
    }
  };

  // Our confirmation page, served from the static domain the vendor was configured with at order-creation
  // time, is cross-origin to this portal and so carries neither this signer's session nor which task was
  // being signed; it asks for both here and reports the outcome to the api itself.
  private answerAuthTokenRequest = (event: MessageEvent) => {
    const signingIframe = document.getElementById('signing-iframe') as HTMLIFrameElement | null;
    if (!signingIframe || event.source !== signingIframe.contentWindow) {
      return;
    }

    const authToken = this._localStorageService.authorizationData?.token;
    if (!this.isTokenRequestOriginAllowlisted(event.origin) || !authToken || this._activeTaskId === null) {
      // Answering with a refusal rather than silence lets our own confirmation page tell the signer the
      // outcome went unrecorded instead of leaving them on a spinner until it times out. An opaque origin
      // cannot be targeted at all, so that requester is left with the timeout.
      if (event.origin !== 'null') {
        (event.source as Window).postMessage({ authTokenDenied: true }, event.origin);
      }
      return;
    }

    const completionContext: ESignCompletionContext = {
      loanDocTaskId: this._activeTaskId,
      portalScope: this.isAgent ? 'Agent' : 'Borrower',
    };
    (event.source as Window).postMessage(
      {
        authCompletionToken: authToken,
        eSignCompletionContext: completionContext,
      },
      event.origin
    );
  };

  private isTokenRequestOriginAllowlisted = (origin: string): boolean => {
    if (origin === window.location.origin) {
      return true;
    }
    const vendorRedirectUrl = this._environmentService.eSignVendorRedirectUrl;
    if (!vendorRedirectUrl) {
      return false;
    }
    try {
      return new URL(vendorRedirectUrl).origin === origin;
    } catch {
      return false;
    }
  };

  constructor(private readonly _portalService: BasePortalService,
    private readonly _spinner: NgxSpinnerService,
    private readonly _navigationService: NavigationService,
    private readonly _notifsService: NotificationService,
    private readonly _domSanitizer: DomSanitizer,
    private readonly _router: Router,
    private readonly _route: ActivatedRoute,
    private readonly _cdr: ChangeDetectorRef,
    private readonly _environmentService: EnvironmentService,
    private readonly _localStorageService: LocalStorageService,
    private readonly _translocoService: TranslocoService) {
    this._route.paramMap.subscribe((params: ParamMap) => {
      this.appId = +params.get('appId');
      const taskId = +params.get('taskId');
      if (taskId && !isNaN(Number(taskId))) {
        this.openSignerView(Number(taskId));
      }
    });
  }

  ngOnInit() {
    this.registerMessageHandler();
    this._route.data.subscribe((data) => {
      this.isAgent = data["isAgent"] as boolean;
    });
  }

  ngOnDestroy(): void {
    this.unregisterMessageHandler();
  }

  private registerMessageHandler = () => {
    if (this._messageHandlerRegistered) {
      return;
    }
    window.addEventListener('message', this._messageHandler, false);
    this._messageHandlerRegistered = true;
  };

  private unregisterMessageHandler = () => {
    if (!this._messageHandlerRegistered) {
      return;
    }
    window.removeEventListener('message', this._messageHandler, false);
    this._messageHandlerRegistered = false;
  };

  private openSignerView = (taskId: number) => {
    this._spinner.show();

    const returnUrl = `${window.location.protocol}//${window.location.host}/e-sign-confirmation/${this.appId}`;

    const request = new OpenSignerViewRequest();
    request.returnUrl = returnUrl;
    request.loanDocTaskId = taskId;

    this.isLoading = true;
    this._portalService.getSigningOrder(taskId).subscribe({
      next: (signingOrderResponse) => {
        this._portalService.openSignerView(taskId, request).subscribe({
          next: async (response) => {
            this.eSignOrder = signingOrderResponse && signingOrderResponse[taskId] && signingOrderResponse[taskId].length > 0 ? signingOrderResponse[taskId][0] : null;
            if (this.eSignOrder?.documentSigningVendor === DocumentSigningVendor.EncompassPOS) {
              await this.prepareSsoCookieForEncompassPos();
            }

            this.signerViewUrl = this._domSanitizer.bypassSecurityTrustResourceUrl(response.signerViewUrl);

            this._activeTaskId = taskId;
            this.isLoading = false;
            this._spinner.hide();
            this._cdr.detectChanges();
          },
          error: (err) => {
            this.onOpenSignerViewErrored(err);
          }
        })
      },
      error: (err) => {
        this.onOpenSignerViewErrored(err);
      }
    });
  }

  // The signing iframe cannot report the outcome to the api itself when the vendor redirect carries no task,
  // so the page hosting the iframe reports it and then closes the iframe the way the other vendors do.
  private reportVendorSigningOutcome = (taskId: number, action: string, vendorQueryParameters: { key: string; value: string }[]) => {
    this._portalService.onSignerViewSubmitted(taskId, action, vendorQueryParameters ?? [])
      .subscribe({
        next: (response) => {
          window.postMessage({
            shouldCloseEsignDocIframe: true,
            response: response
          }, window.location.origin);
        },
        error: (error) => {
          console.error(error?.error?.message ?? "Couldn't report the signing outcome.");
        }
      });
  }

  private onOpenSignerViewErrored = (err: any) => {
    this.isLoading = false;
    this._spinner.hide();
    this._cdr.detectChanges();

    // The api answers 412 when the vendor emailed the signing link to this signer, so signing is only possible
    // from that email - that is an instruction, not an error the signer can do anything about.
    if (err?.status === 412) {
      this._notifsService.showInfo(
        this._translocoService.translate('portal.tasks.docusignEmailMessage'),
        this._translocoService.translate('portal.tasks.eSignDocument'));
    } else {
      this._notifsService.showError("We couldn't open your documents for signing. Please try again or contact your loan team.", 'Error!');
    }

    this._router.navigate([`${this.isAgent ? 'agent' : 'borrower'}-portal/loans`], { queryParams: { companyGuid: this._navigationService.companyGuid } });
  }

  private prepareSsoCookieForEncompassPos = async (): Promise<void> => {
    const eSignSSORedirectUrl = this._environmentService.eSignSSORedirectUrl;
    const jwtToken = this._localStorageService.authorizationData?.token;
    if (!eSignSSORedirectUrl || !jwtToken) {
      return;
    }

    const cookieUrl = `${eSignSSORedirectUrl}?token=${encodeURIComponent(jwtToken)}`;
    const cookieWindow = window.open(cookieUrl, '_blank');
    if (!cookieWindow) {
      return;
    }

    await new Promise<void>((resolve) => {
      const interval = setInterval(() => {
        if (cookieWindow.closed) {
          clearInterval(interval);
          resolve();
        }
      }, 250);
    });
  };
}
